import { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { useToast } from "../../../contexts/ToastContext";
import { Loader2, Calculator, AlertTriangle, Save } from "lucide-react";
import { adminApi } from "../../../lib/api/adminApi"; // Wait, I need to check if this exists or just use fetch
import { supabase } from "../../../lib/supabase";

export function StockSplitManager({ apiPrefix = 'admin' }: { apiPrefix?: string }) {
  const [ticker, setTicker] = useState("");
  const [exDate, setExDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [simulationResult, setSimulationResult] = useState<any>(null);
  const { showToast } = useToast();

  const handleSimulate = async () => {
    if (!ticker || !exDate) {
      showToast({ title: "Error", message: "Ticker and Ex-Date are required.", type: "error" });
      return;
    }

    setIsLoading(true);
    setSimulationResult(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const actualToken = sessionData.session?.access_token;
      if (!actualToken) throw new Error("Missing authentication token. Please log in again.");
      
      const response = await fetch(`${import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001'}/api/${apiPrefix}/stock-split/simulate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${actualToken}`
        },
        body: JSON.stringify({ ticker: ticker.toUpperCase(), exDate })
      });

      const data = await response.json();
      if (!response.ok || data.error) {
        const errorMsg = typeof data.error === 'string' ? data.error : (data.error?.message || data.message || "Failed to simulate");
        throw new Error(errorMsg);
      }

      setSimulationResult(data.data);
      showToast({ title: "Success", message: `Simulation Successful. Calculated Ratio: ${data.data.ratio}`, type: "success" });
    } catch (error: any) {
      console.error(error);
      showToast({ title: "Error", message: error.message, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = async () => {
    if (!ticker || !exDate || !simulationResult) return;

    if (!confirm(`WARNING: This will overwrite ${ticker} data in Azure with a ratio of ${simulationResult.ratio}. This action cannot be undone. Are you sure?`)) {
      return;
    }

    setIsApplying(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const actualToken = sessionData.session?.access_token;
      if (!actualToken) throw new Error("Missing authentication token. Please log in again.");
      
      const response = await fetch(`${import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001'}/api/${apiPrefix}/stock-split/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${actualToken}`
        },
        body: JSON.stringify({ 
          ticker: ticker.toUpperCase(), 
          exDate 
        })
      });

      const data = await response.json();
      if (!response.ok || data.error) {
        const errorMsg = typeof data.error === 'string' ? data.error : (data.error?.message || data.message || "Failed to apply split");
        throw new Error(errorMsg);
      }

      showToast({ title: "Success", message: `Stock split for ${ticker} successfully applied to Azure.`, type: "success" });
      setSimulationResult(null);
    } catch (error: any) {
      console.error(error);
      showToast({ title: "Error", message: error.message, type: "error" });
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5" />
          Stock Split Data Adjustment
        </CardTitle>
        <CardDescription>
          Adjust historical OHLC and Volume data in Azure due to Stock Split or Bonus Issue.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 py-4">
        {/* Baris 1: Input dan Tombol Preview */}
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="ticker" className="text-xs font-semibold shrink-0">Ticker:</Label>
            <Input 
              id="ticker" 
              placeholder="RAJA" 
              value={ticker} 
              onChange={(e) => setTicker(e.target.value)}
              className="h-8 w-24 text-xs"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="exDate" className="text-xs font-semibold shrink-0">Ex-Date:</Label>
            <Input 
              id="exDate" 
              type="date" 
              value={exDate} 
              onChange={(e) => setExDate(e.target.value)}
              className="h-8 w-36 text-xs"
            />
          </div>
          <Button 
            onClick={handleSimulate} 
            disabled={isLoading || !ticker || !exDate}
            className="h-8 text-xs px-3"
            size="sm"
          >
            {isLoading ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Calculator className="h-3 w-3 mr-1.5" />}
            Preview
          </Button>
          <span className="text-[10px] text-muted-foreground hidden sm:inline-block">
            *Membutuhkan 1-2 menit (download data Azure)
          </span>
        </div>

        {/* Baris 2: Hasil Preview dan Tombol OK */}
        {simulationResult && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-muted/30 p-2.5 rounded border border-primary/20">
            <div className="flex items-center gap-3 text-[11px] flex-wrap">
              <span className="font-medium text-amber-600 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> Preview:
              </span>
              <span>Cum: <strong className="text-foreground">{simulationResult.prev_close?.toLocaleString()}</strong></span>
              <span className="text-muted-foreground/50">|</span>
              <span>Ex: <strong className="text-foreground">{simulationResult.close_ex?.toLocaleString()}</strong></span>
              <span className="text-muted-foreground/50">|</span>
              <span>Chg: <strong className="text-red-500">{simulationResult.change_percent}%</strong></span>
              <span className="text-muted-foreground/50">|</span>
              <span>Ref: <strong className="text-foreground">{simulationResult.ref_price?.toLocaleString()}</strong></span>
              <span className="text-muted-foreground/50">|</span>
              <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded font-bold">
                Ratio: 1:{simulationResult.ratio}
              </span>
            </div>
            
            <Button 
              variant="default"
              size="sm"
              onClick={handleApply}
              disabled={isApplying}
              className="h-7 text-xs px-5"
            >
              {isApplying ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Save className="h-3 w-3 mr-1.5" />}
              OK
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
